import { useCallback } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "../../../store/configureStore";
import { BootConfigPayload, loginApiService, uiApiService } from "../../services/api";
import { AuthType, Org, Role } from "../../core";
import appConfig from "../../../appConfig";
import PermissionsManager, { PermissionsInterface } from "../../permissions/PermissionsManager";
import { accessRegister } from "../../permissions/AccessRegister";

import { tenantApiService } from "../../../features/admin/tenants/TenantApiService";
import { AuthCheckError, AuthError, AuthLogout, AuthSuccess, AuthUpdateOrgs } from "./actions";
import { BootConfigUpdate } from "./useBootConfig";
import { AuthState, BootConfigState } from "./types";

const useAuth = () => {
  const dispatch = useDispatch();
  const authState: AuthState = useSelector((storeState: ApplicationState) => ({ ...storeState.auth }), shallowEqual);
  const bootConfigState: BootConfigState = useSelector(
    (storeState: ApplicationState) => ({ ...storeState.bootConfig }),
    shallowEqual
  );

  const logoutUser = useCallback(() => {
    dispatch(AuthLogout());
  }, [dispatch]);

  const postUserLogin = useCallback(
    (bootConfigPayload: BootConfigPayload) => {
      try {
        const { user } = bootConfigPayload;
        let role = Role.Viewer;
        const orgs: Org[] = [];

        const isSupportLoginEnabled = bootConfigPayload?.isSupportLoginEnabled;
        const isSupportSession = bootConfigPayload?.isSupportSession;
        const currentOrg = bootConfigPayload.org;

        role =
          isSupportSession && bootConfigPayload.supportContext
            ? bootConfigPayload.supportContext?.role
            : user?.role || orgs.find(org => org.orgId === currentOrg.id)?.role || role;

        const tempRole = localStorage.getItem(`TENANT_UI_ROLE_${currentOrg.id}`) as Role;
        user.role = tempRole || role;

        user.apptuitRole = role;
        user.isWalkthroughUser = user.email.startsWith("demo_") && user.role === "Viewer";
        user.isBicycleUser =
          user.email.endsWith(`@${appConfig.branding.emailDomain}`) ||
          user.email.endsWith(`@${appConfig.branding.newEmailDomain}`) ||
          user.email === appConfig.smokeTestUserEmail;
        user.permissions = user.permissions || {};
        const permissions: PermissionsInterface = new PermissionsManager(accessRegister.getAccesses(), user);

        //USAGE: permissions.hasAccess(Feature.businessEntity, ActionsMap.addEntity);

        dispatch(
          AuthSuccess({
            user,
            currentOrg,
            orgs,
            permissions
          })
        );
        dispatch(
          BootConfigUpdate({
            user: user,
            org: currentOrg,
            userOrgs: orgs,
            authType: bootConfigPayload.authType.authType,
            uiAuthType: bootConfigPayload.authType,
            ...(isSupportLoginEnabled && { isSupportLoginEnabled }),
            ...(isSupportSession && {
              isSupportSession,
              supportContext: bootConfigPayload?.supportContext
            })
          })
        );
      } catch (error) {
        const { status } = (error as any).response;
        const message = (error as any).response.statusText;
        dispatch(
          AuthError({
            error: {
              message: message,
              statusCode: status
            }
          })
        );
      }
    },
    [dispatch]
  );

  const performAuthCheck = useCallback(async () => {
    try {
      const bootConfigPayload = await tenantApiService.getBootConfig();
      if (bootConfigPayload.authType) {
        bootConfigPayload.authType.authType = bootConfigPayload.authType.authType
          ? bootConfigPayload.authType.authType
          : AuthType.password;
      } else {
        bootConfigPayload.authType = {
          authType: AuthType.password,
          name: AuthType.password
        };
      }

      postUserLogin(bootConfigPayload);
    } catch (error) {
      console.log("error", error);
      const status = (error as any)?.response?.status;
      const message = (error as any)?.response?.statusText;
      dispatch(
        AuthCheckError({
          error: {
            message: message,
            statusCode: status
          }
        })
      );
    }
  }, [dispatch, postUserLogin]);

  const validateCredentials = useCallback(
    async ({ username, password }) => {
      try {
        await loginApiService.apptuitLogin(username, password);
        const bootConfigPayload = await tenantApiService.getBootConfig();
        postUserLogin(bootConfigPayload);
      } catch (error) {
        const { status } = (error as any).response;
        const message = (error as any).response.statusText;
        dispatch(
          AuthError({
            error: {
              message: message,
              statusCode: status
            }
          })
        );
      }
    },
    [dispatch, postUserLogin]
  );

  const getOrgsForSupportUser = useCallback(async () => {
    if (bootConfigState.isSupportLoginEnabled) {
      const orgs = await uiApiService.getAllowedOrgs();
      if (orgs.length) {
        dispatch(AuthUpdateOrgs(orgs));
      }
    }
  }, [bootConfigState, dispatch]);

  return {
    authState,
    getOrgsForSupportUser,
    postUserLogin,
    performAuthCheck,
    logoutUser,
    isSupportLoginEnabled: bootConfigState?.isSupportLoginEnabled || false,
    validateCredentials
  };
};

export { useAuth };
